import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@mlm/ui';
import {
  getActivityFeed,
  getDashboardKpis,
  getEnrollmentTrend,
  getSalesVsCommission,
  type KpiCardData,
} from '../lib/api';
import { BranchPicker } from '../components/branch-picker';
import { useDashboardBranch } from '../lib/use-dashboard-branch';
import { TenantWideBadge } from '../components/dashboard/tenant-wide-badge';
import { MoneySummary } from '../components/dashboard/money-summary';
import { AllTimeSummary } from '../components/dashboard/all-time-summary';
import { CirculatingWallet } from '../components/dashboard/circulating-wallet';
import { AttentionStrip } from '../components/dashboard/attention-strip';
import { DeltaChip } from '../components/dashboard/delta-chip';
import { ActivityFeed } from '../components/dashboard/activity-feed';
import { EnrollmentTrendChart } from '../components/dashboard/enrollment-trend-chart';
import { SalesCommissionChart } from '../components/dashboard/sales-commission-chart';
import { TopEarnersCard, TopRecruitersCard } from '../components/dashboard/leaderboards';
import { SalesByBranchCard } from '../components/dashboard/sales-by-branch-card';
import { formatCount } from '../components/dashboard/format';

/**
 * Admin operations overview. Reads like a ledger top sheet: the month's money
 * first (sales in, commissions out, what's retained), then the two queues an
 * operator must act on, then trend charts, then the activity tape. Member
 * counts stay quiet in the header. Every block loads independently so one slow
 * query never blocks the rest.
 */
/** Poll cadence for every dashboard query — the page stays live without reloads. */
export const DASHBOARD_REFETCH_MS = 45_000;

const WINDOW_OPTIONS = [30, 90, 180, 365] as const;

export function DashboardPage() {
  const [windowDays, setWindowDays] = useState<number>(90);
  // The stacked chart is weekly; derive its window from the same selector.
  const windowWeeks = Math.min(52, Math.max(4, Math.round(windowDays / 7)));

  const branch = useDashboardBranch();
  const { branchId, filtered } = branch;

  const kpisQ = useQuery({
    // Branch rides every key that can narrow, or switching branch would paint
    // the previous one's numbers from cache.
    queryKey: ['dashboard', 'kpis', branchId ?? null],
    queryFn: () => getDashboardKpis(branchId),
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchOnWindowFocus: 'always',
    placeholderData: (prev) => prev,
  });

  const trendQ = useQuery({
    // NOT branch-keyed: `slots` has no branch column, so this series is
    // identical for every branch and switching should reuse the cache.
    queryKey: ['dashboard', 'enrollment-trend', windowDays],
    queryFn: () => getEnrollmentTrend(windowDays),
    refetchInterval: DASHBOARD_REFETCH_MS,
  });
  const stackQ = useQuery({
    queryKey: ['dashboard', 'sales-vs-commission', windowWeeks, branchId ?? null],
    queryFn: () => getSalesVsCommission(windowWeeks, branchId),
    refetchInterval: DASHBOARD_REFETCH_MS,
    placeholderData: (prev) => prev,
  });
  const activityQ = useQuery({
    queryKey: ['dashboard', 'activity', branchId ?? null],
    queryFn: () => getActivityFeed(20, branchId),
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchOnWindowFocus: 'always',
    placeholderData: (prev) => prev,
  });

  const byId = new Map((kpisQ.data?.cards ?? []).map((c) => [c.id, c] as const));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
            Dashboard
          </p>
          <h1 className="text-h1">Operations overview</h1>
        </div>
        {kpisQ.isLoading ? (
          <div className="flex gap-6">
            <Skeleton className="h-11 w-20" />
            <Skeleton className="h-11 w-20" />
          </div>
        ) : (
          <div className="flex items-start gap-6">
            <InlineStat label="Members" stat={byId.get('total_members')} filtered={filtered} />
            <InlineStat label="Active" stat={byId.get('active_members')} filtered={filtered} />
          </div>
        )}
      </div>

      {/* Scope controls, above everything they affect. Branch is primary and
          left-aligned; the trend window is secondary — it drives only the two
          charts, which say so in their own captions. They live together because
          two scope controls at opposite ends of a long page is the complaint
          this filter was added to answer. */}
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <BranchPicker value={branchId} onChange={branch.setBranchId} scopedFallback="label" />
          {filtered && branch.branchLabel ? (
            <p className="pb-2 text-caption text-muted-foreground">
              Showing <span className="font-medium text-foreground">{branch.branchLabel}</span>.
              Figures marked “All branches” have no branch dimension.
              {!branch.isScoped ? (
                <>
                  {' · '}
                  <Button variant="link" className="h-auto p-0 text-caption" onClick={branch.clear}>
                    Clear
                  </Button>
                </>
              ) : null}
            </p>
          ) : null}
          {branch.staleParam ? (
            <p className="pb-2 text-caption text-muted-foreground">
              That branch no longer exists — showing all branches.
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-caption text-muted-foreground">Trend window</span>
          <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
            <SelectTrigger className="h-8 w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOW_OPTIONS.map((d) => (
                <SelectItem key={d} value={String(d)}>
                  Last {d} days
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {kpisQ.isLoading ? (
        <>
          <Card>
            <CardContent className="space-y-4 p-6">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-3 p-6">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          <MoneySummary
            sales={byId.get('mtd_sales')}
            commissions={byId.get('mtd_commissions')}
            filtered={filtered}
          />
          <AllTimeSummary
            sales={byId.get('all_time_sales')}
            earnings={byId.get('all_time_earnings')}
            filtered={filtered}
          />
          <CirculatingWallet wallet={byId.get('circulating_wallet')} filtered={filtered} />
          <AttentionStrip
            orders={byId.get('open_orders')}
            payouts={byId.get('pending_payouts')}
            kyc={byId.get('pending_kyc')}
            filtered={filtered}
            branchId={branchId}
          />
        </>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {trendQ.isLoading || !trendQ.data ? (
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-[260px] w-full" />
            </CardContent>
          </Card>
        ) : (
          <EnrollmentTrendChart
            series={trendQ.data}
            caption={`New members / day, last ${windowDays}d`}
            filtered={filtered}
          />
        )}
        {stackQ.isLoading || !stackQ.data ? (
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-[260px] w-full" />
            </CardContent>
          </Card>
        ) : (
          <SalesCommissionChart
            series={stackQ.data}
            caption={`Last ${windowWeeks} weeks`}
            filtered={filtered}
          />
        )}
      </div>

      <SalesByBranchCard branchId={branchId} branchLabel={branch.branchLabel} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <TopEarnersCard filtered={filtered} />
        <TopRecruitersCard filtered={filtered} />
      </div>

      {activityQ.isLoading || !activityQ.data ? (
        <Card>
          <CardContent className="space-y-3 p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : (
        <ActivityFeed rows={activityQ.data} filtered={filtered} />
      )}
    </div>
  );
}

function InlineStat({
  label,
  stat,
  filtered,
}: {
  label: string;
  stat?: KpiCardData;
  filtered: boolean;
}) {
  return (
    <div className="text-right">
      <div className="flex items-center justify-end gap-1.5">
        <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
          {label}
        </p>
        <TenantWideBadge show={filtered && stat?.scope === 'tenant'} />
      </div>
      <div className="mt-0.5 flex items-center justify-end gap-2">
        <span className="text-h2 tabular-nums">{stat ? formatCount(stat.value) : '0'}</span>
        <DeltaChip deltaPct={stat?.deltaPct ?? null} />
      </div>
    </div>
  );
}
